{/*
  * ORTBIntroductionPage.tsx
  * https://dash.applovin.com/documentation/mediation/auctions/dsps/ortb-spec/introduction
  * Documentation > MAX Demand Partners > DSPs > ORTB Specification > Introduction
  */}
import React, { FunctionComponent } from 'react';
import { AlertMessage, AlertMessageType, DocsPageModule, IndentWidthType, LabelFontFamily } from 'UI-Dash-Commons-UI';
import styled from 'styled-components';

type ORTBIntroductionPageProps = {
    linkTargetTopPosition?: number;
}

const ORTBIntroductionPage: FunctionComponent<ORTBIntroductionPageProps> = ( { linkTargetTopPosition } ) =>
{
    return (
        <>
            <AlertMessage type={AlertMessageType.INFO}>
                <DocsPageModule.DocsPageContentText text={`As of February 14, 2024 Card Games (including real money gambling) categories use the `} />
                <DocsPageModule.DocsPageContentCode children={`IAB9-7`} />
                <DocsPageModule.DocsPageContentCode children={`bcat`} />
                <DocsPageModule.DocsPageContentText text={` value. All DSPs are expected to honor this publisher-specific category block.`} />
            </AlertMessage>
            <DocsPageModule.DocsPageContentSpace withSpace={IndentWidthType.SMALL} />

            <DocsPageModule.DocsPageHeader text={'AppLovin oRTB Specification'}
                                           type={DocsPageModule.DocsPageHeaderType.HEADING_1}
                                           linkTargetTopPosition={linkTargetTopPosition}/>

            <DocsPageModule.DocsPageContent>
                <DocsPageModule.DocsPageContentText
                    text={'AppLovin RTB supports the OpenRTB 2.5 API on ad requests from apps that MAX mediates. '
                        + 'This document describes the OpenRTB integration with AppLovin for SDK bidders and DSPs and details any feature differences between the OpenRTB 2.5 spec and the AppLovin RTB spec.'}
                />
            </DocsPageModule.DocsPageContent>
            <DocsPageModule.DocsPageContent>
                <DocsPageModule.DocsPageContentText text={'Note: '} fontFamily={LabelFontFamily.FONT_FAMILY_DEMI} />
                <DocsPageModule.DocsPageContentText text={'Non-consent and do-not-sell flags passed by publishers to AppLovin in applicable regions are honored, and related user data will not be sent in the bid stream.'} />
            </DocsPageModule.DocsPageContent>

            <DocsPageModule.DocsPageHeader text={'Protocol Details'}
                                           type={DocsPageModule.DocsPageHeaderType.HEADING_2}
                                           linkTargetTopPosition={linkTargetTopPosition}
            />

            <DocsPageModule.DocsPageUnorderedList>
                <DocsPageModule.DocsPageBulletPoint>
                    <DocsPageModule.DocsPageContentText text={'Bid Requests'} fontFamily={LabelFontFamily.FONT_FAMILY_DEMI}/>
                    <DocsPageModule.DocsPageContentText text={' — AppLovin RTB buyers receive bid requests in HTTP '}/>
                    <DocsPageModule.DocsPageContentCode children={'POST'} />
                    <DocsPageModule.DocsPageContentText text={' format. AppLovin recommends that you use HTTP so as to avoid latency.'}/>
                </DocsPageModule.DocsPageBulletPoint>

                <DocsPageModule.DocsPageBulletPoint>
                    <DocsPageModule.DocsPageContentText text={'HTTP Keep-Alive'} fontFamily={LabelFontFamily.FONT_FAMILY_DEMI}/>
                    <DocsPageModule.DocsPageContentText
                        text={' — AppLovin RTB buyers should enable persistent connection handling. Set max keep-alive to at least five minutes.'}/>
                </DocsPageModule.DocsPageBulletPoint>

                <DocsPageModule.DocsPageBulletPoint>
                    <DocsPageModule.DocsPageContentText text={'Compressed/gzip oRTB'} fontFamily={LabelFontFamily.FONT_FAMILY_DEMI}/>
                    <DocsPageModule.DocsPageContentText text={' — AppLovin RTB requires compression for efficiency. Adhere to '}/>
                    <DocsPageModule.DocsPageContentAnchor
                        to={'https://www.iab.com/wp-content/uploads/2016/03/OpenRTB-API-Specification-Version-2-5-FINAL.pdf#%5B%7B%22num%22%3A53%2C%22gen%22%3A0%7D%2C%7B%22name%22%3A%22XYZ%22%7D%2C69%2C229%2C0%5D'}
                        text={'OpenRTB API Specification version 2.5 section 2.4: Data Encoding'}
                        newWindow={true}
                    />
                    <DocsPageModule.DocsPageContentText text={'.'}/>
                </DocsPageModule.DocsPageBulletPoint>
            </DocsPageModule.DocsPageUnorderedList>

            <DocsPageModule.DocsPageHeader text={'Bid Regions'} type={DocsPageModule.DocsPageHeaderType.HEADING_2}
                                           linkTargetTopPosition={linkTargetTopPosition}
            />
            <DocsPageModule.DocsPageContent>
                <DocsPageModule.DocsPageContentText text={'The AppLovin RTB servers cover four geographical regions:'}/>
            </DocsPageModule.DocsPageContent>

            <DocsPageModule.DocsPageUnorderedList>
                <DocsPageModule.DocsPageBulletPoint>
                    <DocsPageModule.DocsPageContentText text={'ASIA-PACIFIC: Hong Kong, Tokyo'}/>
                </DocsPageModule.DocsPageBulletPoint>
                <DocsPageModule.DocsPageBulletPoint>
                    <DocsPageModule.DocsPageContentText text={'EUROPE: Amsterdam'}/>
                </DocsPageModule.DocsPageBulletPoint>
                <DocsPageModule.DocsPageBulletPoint>
                    <DocsPageModule.DocsPageContentText text={'US-EAST: New York, Washington'}/>
                </DocsPageModule.DocsPageBulletPoint>
                <DocsPageModule.DocsPageBulletPoint>
                    <DocsPageModule.DocsPageContentText text={'US-WEST: San Francisco, San Jose'}/>
                </DocsPageModule.DocsPageBulletPoint>
            </DocsPageModule.DocsPageUnorderedList>

            <DocsPageModule.DocsPageHeader text={'Endpoints'} type={DocsPageModule.DocsPageHeaderType.HEADING_2}
                                           linkTargetTopPosition={linkTargetTopPosition}/>
            <DocsPageModule.DocsPageContent>
                <DocsPageModule.DocsPageContentText text={'DSP buyers and SDK bidders can specify up to one endpoint per region. '
                                                        + 'All requests from the respective region will be routed to that endpoint.' } />
            </DocsPageModule.DocsPageContent>

            <DocsPageModule.DocsPageHeader text={'Supported Ad Formats'} type={DocsPageModule.DocsPageHeaderType.HEADING_2}
                                           linkTargetTopPosition={linkTargetTopPosition}/>
            <DocsPageModule.DocsPageContentText
                text={'AppLovin RTB supports mobile in-app ads only; it does not support desktop or web ad serving. '
                + 'The ad formats AppLovin RTB supports are banners, medium rectangles (MRECs), native ads, interstitials, and rewarded ads.'}/>

            <DocsPageModule.DocsPageContent>
                <DocsPageModule.DocsPageContentText text={'Acceptable MIME types include:'}/>
                <DocsPageModule.DocsPageUnorderedList>
                    <DocsPageModule.DocsPageBulletPoint>
                        <DocsPageModule.DocsPageContentText text={'MRAID, HTML, MREC: '}/>
                        <DocsPageModule.DocsPageContentCode children={'text/html'}/>
                    </DocsPageModule.DocsPageBulletPoint>
                    <DocsPageModule.DocsPageBulletPoint>
                        <DocsPageModule.DocsPageContentText text={'Native ads: '} />
                        <DocsPageModule.DocsPageContentCode children={'image/png'} />
                        <DocsPageModule.DocsPageContentText text={', '} />
                        <DocsPageModule.DocsPageContentCode children={'image/jpeg'} />
                        <DocsPageModule.DocsPageContentText text={', '} />
                        <DocsPageModule.DocsPageContentCode children={'image/gif'} />
                    </DocsPageModule.DocsPageBulletPoint>
                    <DocsPageModule.DocsPageBulletPoint>
                        <DocsPageModule.DocsPageContentText text={'VAST: '}/>
                        <DocsPageModule.DocsPageContentCode children={'video/mp4'}/>
                    </DocsPageModule.DocsPageBulletPoint>
                </DocsPageModule.DocsPageUnorderedList>
            </DocsPageModule.DocsPageContent>

            <DocsPageModule.DocsPageContent>
                <DocsPageModule.DocsPageContentText
                    text={'Acceptable MRAID and VAST versions include those shown in the list below. '
                    + 'However, there are limitations. '
                    + 'For example, AppLovin RTB does not support calendar API events from MRAID.'}/>

                <MultiColumnList>
                    <DocsPageModule.DocsPageBulletPoint>
                        <DocsPageModule.DocsPageContentText text={'MRAID 1.0'}/>
                    </DocsPageModule.DocsPageBulletPoint>
                    <DocsPageModule.DocsPageBulletPoint>
                        <DocsPageModule.DocsPageContentText text={'MRAID 2.0'}/>
                    </DocsPageModule.DocsPageBulletPoint>
                    <DocsPageModule.DocsPageBulletPoint>
                        <DocsPageModule.DocsPageContentText text={'MRAID 3.0'}/>
                    </DocsPageModule.DocsPageBulletPoint>
                    <DocsPageModule.DocsPageBulletPoint>
                        <DocsPageModule.DocsPageContentText text={'VAST 2.0'}/>
                    </DocsPageModule.DocsPageBulletPoint>
                    <DocsPageModule.DocsPageBulletPoint>
                        <DocsPageModule.DocsPageContentText text={'VAST 3.0'}/>
                    </DocsPageModule.DocsPageBulletPoint>
                    <DocsPageModule.DocsPageBulletPoint>
                        <DocsPageModule.DocsPageContentText text={'VAST 4.0'}/>
                    </DocsPageModule.DocsPageBulletPoint>
                    <DocsPageModule.DocsPageBulletPoint>
                        <DocsPageModule.DocsPageContentText text={'VAST 4.1'}/>
                    </DocsPageModule.DocsPageBulletPoint>
                    <DocsPageModule.DocsPageBulletPoint>
                        <DocsPageModule.DocsPageContentText text={'VAST 4.2'}/>
                    </DocsPageModule.DocsPageBulletPoint>
                </MultiColumnList>

                <DocsPageModule.DocsPageContent
                    text={'There is additional information further down on this page concerning MRAID, native ads, and VAST support.'}/>
            </DocsPageModule.DocsPageContent>

            <DocsPageModule.DocsPageHeader text={'Supported Ad Sizes'}
                                           type={DocsPageModule.DocsPageHeaderType.HEADING_2}
                                           linkTargetTopPosition={linkTargetTopPosition}/>

            <DocsPageModule.DocsPageContent>
                <DocsPageModule.DocsPageContentText text={'AppLovin RTB supports the following ad sizes:'}/>

                <DocsPageModule.DocsPageUnorderedList>
                    <DocsPageModule.DocsPageBulletPoint>
                        <DocsPageModule.DocsPageContentText text={'Video object (VAST)'}/>
                    </DocsPageModule.DocsPageBulletPoint>

                    <DocsPageModule.DocsPageUnorderedList>
                        <DocsPageModule.DocsPageBulletPoint>
                            <DocsPageModule.DocsPageContentText text={'320×480'}/>
                        </DocsPageModule.DocsPageBulletPoint>
                        <DocsPageModule.DocsPageBulletPoint>
                            <DocsPageModule.DocsPageContentText text={'480×320'}/>
                        </DocsPageModule.DocsPageBulletPoint>
                        <DocsPageModule.DocsPageBulletPoint>
                            <DocsPageModule.DocsPageContentText text={'768×1024'}/>
                        </DocsPageModule.DocsPageBulletPoint>
                        <DocsPageModule.DocsPageBulletPoint>
                            <DocsPageModule.DocsPageContentText text={'1024×768'}/>
                        </DocsPageModule.DocsPageBulletPoint>
                        <DocsPageModule.DocsPageBulletPoint>
                            <DocsPageModule.DocsPageContentText text={'1920×1080 (Connected TV)'}/>
                        </DocsPageModule.DocsPageBulletPoint>
                    </DocsPageModule.DocsPageUnorderedList>

                    <DocsPageModule.DocsPageBulletPoint>
                        <DocsPageModule.DocsPageContentText text={'Banner object (HTML, MRAID)'}/>
                    </DocsPageModule.DocsPageBulletPoint>
                    <DocsPageModule.DocsPageUnorderedList>
                        <DocsPageModule.DocsPageBulletPoint>
                            <DocsPageModule.DocsPageContentText text={'320×50'}/>
                        </DocsPageModule.DocsPageBulletPoint>
                        <DocsPageModule.DocsPageBulletPoint>
                            <DocsPageModule.DocsPageContentText text={'728×90 (Leader)'}/>
                        </DocsPageModule.DocsPageBulletPoint>
                        <DocsPageModule.DocsPageBulletPoint>
                            <DocsPageModule.DocsPageContentText text={'300×250 (MREC)'}/>
                        </DocsPageModule.DocsPageBulletPoint>
                    </DocsPageModule.DocsPageUnorderedList>

                    <DocsPageModule.DocsPageBulletPoint>
                        <DocsPageModule.DocsPageContentText text={'Fullscreen object (HTML, MRAID)'}/>
                    </DocsPageModule.DocsPageBulletPoint>
                    <DocsPageModule.DocsPageUnorderedList>
                        <DocsPageModule.DocsPageBulletPoint>
                            <DocsPageModule.DocsPageContentText text={'320×480'}/>
                        </DocsPageModule.DocsPageBulletPoint>
                        <DocsPageModule.DocsPageBulletPoint>
                            <DocsPageModule.DocsPageContentText text={'480×320'}/>
                        </DocsPageModule.DocsPageBulletPoint>
                        <DocsPageModule.DocsPageBulletPoint>
                            <DocsPageModule.DocsPageContentText text={'768×1024 (Portrait)'}/>
                        </DocsPageModule.DocsPageBulletPoint>
                        <DocsPageModule.DocsPageBulletPoint>
                            <DocsPageModule.DocsPageContentText text={'1024×768 (Landscape)'}/>
                        </DocsPageModule.DocsPageBulletPoint>
                    </DocsPageModule.DocsPageUnorderedList>

                    <DocsPageModule.DocsPageBulletPoint>
                        <DocsPageModule.DocsPageContentText text={'Native object (specific instructions below)'} />
                    </DocsPageModule.DocsPageBulletPoint>
                </DocsPageModule.DocsPageUnorderedList>
            </DocsPageModule.DocsPageContent>

            <DocsPageModule.DocsPageHeader text={'Exchange Currency'}
                                           type={DocsPageModule.DocsPageHeaderType.HEADING_2}
                                           linkTargetTopPosition={linkTargetTopPosition}/>

            <DocsPageModule.DocsPageContent
                text={'AppLovin RTB Exchange supports only USD. It ignores the currency attribute \`cur\` .'}/>

            <DocsPageModule.DocsPageHeader text={'Auction Type'} type={DocsPageModule.DocsPageHeaderType.HEADING_2}
                                           linkTargetTopPosition={linkTargetTopPosition}/>

            <DocsPageModule.DocsPageContent
                text={'MAX auction runs on a first-price basis. '
                + 'AppLovin expects bidders to pay the CPM amount they bid in full. '}/>

            <DocsPageModule.DocsPageHeader text={'Blocked Creative Types'}
                                           type={DocsPageModule.DocsPageHeaderType.HEADING_2}
                                           linkTargetTopPosition={linkTargetTopPosition}/>

            <DocsPageModule.DocsPageContent>
                <DocsPageModule.DocsPageContentText text={'AppLovin RTB blocks the following creative types:'}/>
                <DocsPageModule.DocsPageUnorderedList>
                    <DocsPageModule.DocsPageBulletPoint>
                        <DocsPageModule.DocsPageContentText text={'Adobe Flash'}/>
                    </DocsPageModule.DocsPageBulletPoint>
                    <DocsPageModule.DocsPageBulletPoint>
                        <DocsPageModule.DocsPageContentText text={'Audio Ad (Auto-Play)'}/>
                    </DocsPageModule.DocsPageBulletPoint>
                    <DocsPageModule.DocsPageBulletPoint>
                        <DocsPageModule.DocsPageContentText text={'Audio Ad (User Initiated)'}/>
                    </DocsPageModule.DocsPageBulletPoint>
                    <DocsPageModule.DocsPageBulletPoint>
                        <DocsPageModule.DocsPageContentText text={'Expandable (User Initiated — Rollover)'}/>
                    </DocsPageModule.DocsPageBulletPoint>
                    <DocsPageModule.DocsPageBulletPoint>
                        <DocsPageModule.DocsPageContentText text={'Pop (e.g. Over, Under, or Upon Exit)'}/>
                    </DocsPageModule.DocsPageBulletPoint>
                    <DocsPageModule.DocsPageBulletPoint>
                        <DocsPageModule.DocsPageContentText text={'Provocative or Suggestive Imagery'}/>
                    </DocsPageModule.DocsPageBulletPoint>
                    <DocsPageModule.DocsPageBulletPoint>
                        <DocsPageModule.DocsPageContentText text={'Windows Dialog or Alert Style'}/>
                    </DocsPageModule.DocsPageBulletPoint>
                </DocsPageModule.DocsPageUnorderedList>
            </DocsPageModule.DocsPageContent>

            <DocsPageModule.DocsPageContent>
                <DocsPageModule.DocsPageContentText
                    text={'If the publisher does not support playable ads, then AppLovin RTB also blocks the following creative types:'}/>
                <DocsPageModule.DocsPageUnorderedList>
                    <DocsPageModule.DocsPageBulletPoint>
                        <DocsPageModule.DocsPageContentText text={'Expandable (Automatic) [only for non-banner/MREC]'}/>
                    </DocsPageModule.DocsPageBulletPoint>
                    <DocsPageModule.DocsPageBulletPoint>
                        <DocsPageModule.DocsPageContentText text={'Expandable (User Initiated — Click) [only for non-banner/MREC]'}/>
                    </DocsPageModule.DocsPageBulletPoint>
                    <DocsPageModule.DocsPageBulletPoint>
                        <DocsPageModule.DocsPageContentText text={'User Interactive (e.g. Embedded Games)'}/>
                    </DocsPageModule.DocsPageBulletPoint>
                </DocsPageModule.DocsPageUnorderedList>
            </DocsPageModule.DocsPageContent>

            <DocsPageModule.DocsPageHeader text={'Blocked Categories'}
                                           type={DocsPageModule.DocsPageHeaderType.HEADING_2}
                                           linkTargetTopPosition={linkTargetTopPosition}/>

            <DocsPageModule.DocsPageContent>
                <DocsPageModule.DocsPageContentText text={'AppLovin RTB blocks the following IAB categories:'}/>
                <DocsPageModule.DocsPageUnorderedList>
                    <DocsPageModule.DocsPageBulletPoint>
                        <DocsPageModule.DocsPageContent text={'\`IAB8-5\` (Cocktails/Beer)'}/>
                    </DocsPageModule.DocsPageBulletPoint>
                    <DocsPageModule.DocsPageBulletPoint>
                        <DocsPageModule.DocsPageContent text={'\`IAB8-18\` (Wine)'}/>
                    </DocsPageModule.DocsPageBulletPoint>
                    <DocsPageModule.DocsPageBulletPoint>
                        <DocsPageModule.DocsPageContent text={'\`IAB11-4\` (Politics)'}/>
                    </DocsPageModule.DocsPageBulletPoint>
                    <DocsPageModule.DocsPageBulletPoint>
                        <DocsPageModule.DocsPageContent text={'\`IAB25\` (Non-Standard Content)'}/>
                    </DocsPageModule.DocsPageBulletPoint>
                    <DocsPageModule.DocsPageBulletPoint>
                        <DocsPageModule.DocsPageContent text={'\`IAB26\` (Illegal Content)'}/>
                    </DocsPageModule.DocsPageBulletPoint>
                </DocsPageModule.DocsPageUnorderedList>
            </DocsPageModule.DocsPageContent>

            <DocsPageModule.DocsPageHeader text={'Blocked Ad Types'}
                                           type={DocsPageModule.DocsPageHeaderType.HEADING_2}
                                           linkTargetTopPosition={linkTargetTopPosition}/>

            <DocsPageModule.DocsPageContent>
                <DocsPageModule.DocsPageContent text={'AppLovin RTB blocks the following ad types:'}/>
                <DocsPageModule.DocsPageUnorderedList>
                    <DocsPageModule.DocsPageBulletPoint>
                        <DocsPageModule.DocsPageContent text={'flash'}/>
                    </DocsPageModule.DocsPageBulletPoint>
                    <DocsPageModule.DocsPageBulletPoint>
                        <DocsPageModule.DocsPageContent text={'iframe'}/>
                    </DocsPageModule.DocsPageBulletPoint>
                    <DocsPageModule.DocsPageBulletPoint>
                        <DocsPageModule.DocsPageContent text={'XHTML Text Ad'}/>
                    </DocsPageModule.DocsPageBulletPoint>
                </DocsPageModule.DocsPageUnorderedList>
            </DocsPageModule.DocsPageContent>

        </>
    )
}

const MultiColumnList = styled( DocsPageModule.DocsPageUnorderedList )`
   column-count: 3;
`;

export default ORTBIntroductionPage;
